// Unused or unreferenced check for delete
import { supabase } from './supabase'
import type { 
  Game, 
  GameScore, 
  PowerUp, 
  PlayerPowerUp, 
  GameStreak, 
  PlayerAchievement 
} from '@/types/games'

export const GAME_MODES: Record<string, any> = {
  trivia: {
    id: 'trivia',
    name: 'Trivia Challenge',
    description: 'Test your knowledge with challenging questions',
    questionType: 'multiple_choice',
    minPlayers: 1,
    maxPlayers: 100,
    defaultTimeLimit: 30,
    pointsPerCorrect: 1000,
    timeBonus: true,
    streakBonus: true,
    powerUpsEnabled: true,
    allowHints: true,
    roundsPerGame: 5
  },
  memory: {
    id: 'memory',
    name: 'Memory Match',
    description: 'Match pairs of cards to test your memory',
    questionType: 'memory_match',
    minPlayers: 1,
    maxPlayers: 4,
    defaultTimeLimit: 120,
    pointsPerCorrect: 500,
    timeBonus: true,
    streakBonus: false,
    powerUpsEnabled: false,
    allowHints: false,
    roundsPerGame: 1,
    livesPerGame: 3
  },
  word_puzzle: {
    id: 'word_puzzle',
    name: 'Word Puzzle',
    description: 'Solve word puzzles and expand your vocabulary',
    questionType: 'word_scramble',
    minPlayers: 1,
    maxPlayers: 1,
    defaultTimeLimit: 180,
    pointsPerCorrect: 750,
    timeBonus: false,
    streakBonus: false,
    powerUpsEnabled: true,
    allowHints: true,
    roundsPerGame: 3
  },
  sequence: {
    id: 'sequence',
    name: 'Sequence Challenge',
    description: 'Arrange items in the correct order',
    questionType: 'sequence',
    minPlayers: 1,
    maxPlayers: 50,
    defaultTimeLimit: 60,
    pointsPerCorrect: 1000,
    timeBonus: true,
    streakBonus: true,
    powerUpsEnabled: true,
    allowHints: true,
    roundsPerGame: 5
  }
};

const MAX_RETRIES = 3
const RETRY_DELAY = 1000

async function retryOperation<T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = RETRY_DELAY
): Promise<T> {
  try {
    const result = await operation()
    return result
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay))
      return retryOperation(operation, retries - 1, delay)
    }
    throw error
  }
}

export async function getDailyGame(): Promise<Game | null> {
  try {
    // First check for an existing daily game
    const { data, error } = await supabase
        .from('games')
        .select('*')
        .eq('is_daily', true)
        .eq('status', 'open')
        .maybeSingle()

    if (error) {
      if (error.code !== 'PGRST116') {
        console.error('Error fetching daily game:', error)
        return null
      }
    }

    // If no daily game exists, create one
    if (!data) {
      const { data: newGame, error: createError } = await supabase
        .rpc('create_daily_game')

      if (createError) {
        console.error('Error creating daily game:', createError)
        return null
      }

      // Fetch the newly created game
      const { data: createdGame, error: fetchError } = await supabase
        .from('games')
        .select('*')
        .eq('id', newGame)
        .single()

      if (fetchError) {
        console.error('Error fetching new daily game:', fetchError)
        return null
      }

      return createdGame
    }

    return data || null
  } catch (error) {
    console.error('Error fetching daily game:', error)
    return null
  }
}

export async function fetchGames(): Promise<Game[]> {
  try {
    const { data, error } = await retryOperation(async () => {
      const response = await supabase
        .from('games')
        .select('*')
        .eq('status', 'open')
        .eq('is_daily', false)
        .order('created_at', { ascending: false })
      return response
    })

    if (error) {
      console.error('Error fetching games:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('Error fetching games:', error)
    return []
  }
}

export async function fetchGameScores(gameId: string): Promise<GameScore[]> {
  try {
    const { data, error } = await retryOperation(async () => {
      const response = await supabase
        .from('game_scores')
        .select('*, profiles:user_id (username)')
        .eq('game_id', gameId)
        .order('score', { ascending: false })
      return response
    })

    if (error) {
      console.error('Error fetching game scores:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('Error fetching game scores:', error)
    return []
  }
}

export async function canAffordGame(userId: string, gameId: string): Promise<boolean> {
  try {
    const { data, error } = await retryOperation(async () => {
      const response = await supabase
        .rpc('can_afford_game', {
          user_id: userId,
          game_id: gameId
        })
      return response
    })

    if (error) {
      console.error('Error checking game affordability:', error)
      return false
    }

    return !!data
  } catch (error) {
    console.error('Error checking game affordability:', error)
    return false
  }
}

export async function startGame(userId: string, gameId: string): Promise<boolean> {
  try {
    const { data, error } = await retryOperation(async () => {
      const response = await supabase
        .rpc('start_game', {
          user_id: userId,
          game_id: gameId
        })
      return response
    })

    if (error) {
      console.error('Error starting game:', error)
      return false
    }

    return !!data
  } catch (error) {
    console.error('Error starting game:', error)
    return false
  }
}

// Unused or unreferenced check for delete
export async function getPowerUps(): Promise<PowerUp[]> {
  try {
    const { data, error } = await retryOperation(async () => {
      const response = await supabase
        .from('game_powerups')
        .select('*')
        .order('cost', { ascending: true })
      return response
    })

    if (error) {
      console.error('Error fetching power-ups:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('Error fetching power-ups:', error)
    return []
  }
}

export async function getPlayerPowerUps(userId: string): Promise<PlayerPowerUp[]> {
  try {
    const { data, error } = await retryOperation(async () => {
      const response = await supabase
        .from('player_powerups')
        .select(`
          *,
          powerup:powerup_id (*)
        `)
        .eq('user_id', userId)
      return response
    })

    if (error) {
      console.error('Error fetching player power-ups:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('Error fetching player power-ups:', error)
    return []
  }
}

// Unused or unreferenced check for delete
export async function buyPowerUp(userId: string, powerUpId: string): Promise<boolean> {
  try {
    const { data: powerUp, error: powerUpError } = await supabase
      .from('game_powerups')
      .select('cost')
      .eq('id', powerUpId)
      .single()

    if (powerUpError || !powerUp) {
      console.error('Error fetching power-up:', powerUpError)
      return false
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('voy_coins')
      .eq('id', userId)
      .single()

    if (profileError || !profile) {
      console.error('Error fetching profile:', profileError)
      return false
    }

    if (profile.voy_coins < powerUp.cost) {
      return false
    }

    const { error: purchaseError } = await supabase.rpc('purchase_powerup', {
      user_id: userId,
      powerup_id: powerUpId,
      cost: powerUp.cost
    })

    if (purchaseError) {
      console.error('Error purchasing power-up:', purchaseError)
      return false
    }

    return true
  } catch (error) {
    console.error('Error buying power-up:', error)
    return false
  }
}

// Unused or unreferenced check for delete
export async function usePowerUp(
  userId: string,
  powerUpId: string,
  gameId: string
): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('use_powerup', {
      user_id: userId,
      powerup_id: powerUpId,
      game_id: gameId
    })

    if (error) {
      console.error('Error using power-up:', error)
      return false
    }

    return true
  } catch (error) {
    console.error('Error using power-up:', error)
    return false
  }
}

// Unused or unreferenced check for delete
export async function getGameStreak(
  userId: string,
  gameId: string
): Promise<GameStreak | null> {
  try {
    const { data, error } = await supabase
      .from('game_streaks')
      .select('*')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .single()

    if (error) {
      if (error.code !== 'PGRST116') {
        console.error('Error fetching game streak:', error)
      }
      return null
    }

    return data
  } catch (error) {
    console.error('Error fetching game streak:', error)
    return null
  }
}

// Unused or unreferenced check for delete
export async function getPlayerAchievements(userId: string): Promise<PlayerAchievement[]> {
  try {
    const { data, error } = await supabase
      .from('player_achievements')
      .select(`
        *,
        achievement:achievement_id (*)
      `)
      .eq('user_id', userId)
      .order('earned_at', { ascending: false })

    if (error) {
      console.error('Error fetching player achievements:', error)
      return []
    }

    return data || []
  } catch (error) {
    console.error('Error fetching player achievements:', error)
    return []
  }
}